import { cn } from '@parcel/ui';
import { Building2, GraduationCap } from 'lucide-react';
import * as React from 'react';
import type { ApiContactDetail } from '../../lib/contacts.js';
import {
  type ContactProfileView,
  type EducationEntry,
  type EmploymentRole,
  LOOKUP_RETRY_DAYS,
  shortDate,
  toContactProfileView,
  windowEnd,
} from '../../lib/enrich.js';
import { logoUrl } from '../../lib/logo.js';
import { useEnrichScope } from '../enrich/enrich-scope.js';
import { RevealAction, revealMissedAt, revealOffered } from '../enrich/reach-cell.js';
import { RevealMissed } from '../enrich/reveal-button.js';
import { EmptyTabState } from '../entity-drawer/entity-drawer-sections.js';
import { ExpandableText } from '../entity-drawer/expandable-text.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ISO = /^(\d{4})(?:-(\d{2}))?/;

/** A provider date's year and month; a year before 1900 is a placeholder, not a date. */
function dateParts(value: string | null): { year: number; month: number | null } | null {
  const match = value ? ISO.exec(value) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) : null;
  return year < 1900 ? null : { year, month: month && month >= 1 && month <= 12 ? month : null };
}

function dateLabel(value: string | null, yearOnly: boolean): string | null {
  if (!value) return null;
  const parts = dateParts(value);
  // Free text from the provider is shown as written; a placeholder date is not shown.
  if (!parts) return ISO.test(value) ? null : value;
  return yearOnly || parts.month == null
    ? String(parts.year)
    : `${MONTHS[parts.month - 1]} ${parts.year}`;
}

/** "Jul 2023 – Present", or years alone ("2009 – 2013") for schooling. */
export function rangeLabel(
  start: string | null,
  end: string | null,
  opts: { yearOnly?: boolean; openEnded?: boolean } = {},
): string {
  const from = dateLabel(start, opts.yearOnly ?? false);
  const to = dateLabel(end, opts.yearOnly ?? false) ?? (opts.openEnded && from ? 'Present' : null);
  return [from, to].filter(Boolean).join(' – ');
}

/** Whole months counted the way profiles count them: both end months included. */
export function durationLabel(
  start: string | null,
  end: string | null,
  now: Date = new Date(),
): string | null {
  const from = dateParts(start);
  const to = end ? dateParts(end) : { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
  if (!from?.month || !to?.month) return null;
  const months = (to.year - from.year) * 12 + (to.month - from.month) + 1;
  if (months < 1) return null;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [
    years ? `${years} yr${years > 1 ? 's' : ''}` : '',
    rest ? `${rest} mo${rest > 1 ? 's' : ''}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

const FIRM_SUFFIX = /\b(inc|llc|llp|ltd|co|corp|corporation|company|group|the)\b/g;

function firmKey(name: string | null): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(FIRM_SUFFIX, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** One firm by name, a whole-word prefix counting ("Turner" and "Turner Construction"). */
function sameFirmName(a: string | null, b: string | null): boolean {
  const left = firmKey(a);
  const right = firmKey(b);
  return (
    Boolean(left && right) &&
    (left === right || left.startsWith(`${right} `) || right.startsWith(`${left} `))
  );
}

/** Two roles at one firm: by LinkedIn id when both carry one, else by name. */
function sameFirm(a: EmploymentRole, b: EmploymentRole): boolean {
  if (a.firm_linkedin_id && b.firm_linkedin_id) return a.firm_linkedin_id === b.firm_linkedin_id;
  return sameFirmName(a.firm_name, b.firm_name);
}

interface CompanyGroup {
  firm: string | null;
  roles: EmploymentRole[];
}

/** Back-to-back roles at one firm read as one block, as a profile shows a promotion. */
export function groupByCompany(roles: readonly EmploymentRole[]): CompanyGroup[] {
  const groups: CompanyGroup[] = [];
  for (const role of roles) {
    const last = groups.at(-1);
    // Measured against the block's first role, and never across two LinkedIn ids.
    const head = last?.roles[0];
    const idClash =
      role.firm_linkedin_id &&
      last?.roles.some((r) => r.firm_linkedin_id && r.firm_linkedin_id !== role.firm_linkedin_id);
    if (last && head && role.firm_name && !idClash && sameFirm(head, role)) last.roles.push(role);
    else groups.push({ firm: role.firm_name, roles: [role] });
  }
  return groups;
}

function Mark({
  website,
  icon: Icon,
  className,
}: {
  website?: string | null;
  icon: typeof Building2;
  className?: string;
}) {
  const [failed, setFailed] = React.useState(false);
  const src = website && !failed ? logoUrl(website) : null;
  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted text-muted-foreground',
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="size-full object-contain"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon className="size-5" aria-hidden />
      )}
    </span>
  );
}

function RoleLines({ role, showFirm }: { role: EmploymentRole; showFirm: boolean }) {
  // A past role with no end date reads by its start alone, never as Present.
  const range = rangeLabel(role.start, role.end, { openEnded: role.current });
  const span = role.end || role.current ? durationLabel(role.start, role.end) : null;
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="font-medium text-foreground">{role.title ?? 'Role not given'}</div>
      {showFirm && role.firm_name ? (
        <div className="text-foreground">
          {[role.firm_name, role.contract_type].filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {range ? (
        <div className="text-muted-foreground">{[range, span].filter(Boolean).join(' · ')}</div>
      ) : null}
      {role.location ? <div className="text-muted-foreground">{role.location}</div> : null}
      {role.description ? (
        <ExpandableText
          text={role.description}
          collapsedLines={3}
          className="pt-1.5 leading-6 text-foreground"
          toggleClassName="ml-0 mt-1"
        />
      ) : null}
    </div>
  );
}

function CompanyBlock({ group, website }: { group: CompanyGroup; website: string | null }) {
  const [first] = group.roles;
  if (!first) return null;
  if (group.roles.length === 1) {
    return (
      <li className="flex gap-3">
        <Mark website={website} icon={Building2} />
        <RoleLines role={first} showFirm />
      </li>
    );
  }
  const earliest = group.roles.at(-1) ?? first;
  const total = first.end || first.current ? durationLabel(earliest.start, first.end) : null;
  return (
    <li className="flex gap-3">
      <Mark website={website} icon={Building2} />
      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{group.firm}</div>
          {total ? <div className="text-muted-foreground">{total}</div> : null}
        </div>
        <ol className="space-y-4 border-l pl-4">
          {group.roles.map((role, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: provider roles carry no id and may repeat.
            <li key={index} className="relative">
              <span className="absolute -left-[21px] top-1.5 size-2.5 rounded-full border-2 border-background bg-muted-foreground/40" />
              <RoleLines role={role} showFirm={false} />
            </li>
          ))}
        </ol>
      </div>
    </li>
  );
}

function School({ entry }: { entry: EducationEntry }) {
  const study = [entry.degree, entry.fields_of_study.join(', ')].filter(Boolean).join(', ');
  const years = rangeLabel(entry.start, entry.end, { yearOnly: true });
  return (
    <li className="flex gap-3">
      <Mark icon={GraduationCap} />
      <div className="min-w-0 space-y-0.5">
        <div className="font-medium text-foreground">{entry.school ?? 'School not given'}</div>
        {study ? <div className="text-foreground">{study}</div> : null}
        {years ? <div className="text-muted-foreground">{years}</div> : null}
      </div>
    </li>
  );
}

/** Pills as the account portfolio draws them, with an optional muted note. */
function Chips({ items }: { items: Array<{ key: string; label: string; note?: string | null }> }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span key={item.key} className="rounded-full border px-2 py-0.5 text-xs text-foreground">
          {item.label}
          {item.note ? <span className="ml-1 text-muted-foreground">{item.note}</span> : null}
        </span>
      ))}
    </div>
  );
}

function Block({
  title,
  aside,
  children,
}: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** The current role from the free facts, for a person whose full profile is not revealed. */
function currentRoleOnly(contact: ApiContactDetail): EmploymentRole[] {
  const role = contact.current_role;
  if (!role?.title && !role?.employer_name) return [];
  return [
    {
      title: role.title,
      firm_name: role.employer_name,
      location: null,
      start: role.started_on,
      end: null,
      description: role.description,
      contract_type: null,
      current: true,
      firm_website: null,
    },
  ];
}

/** The Experience tab: employment, education, skills and languages, laid out like a profile. */
export function ContactExperience({ contact }: { contact: ApiContactDetail }) {
  const { workspaceId, status } = useEnrichScope();
  const profile = contact.reveals?.profile ?? null;
  const view: ContactProfileView | null = profile ? toContactProfileView(profile) : null;
  const employment = view ? view.employment : currentRoleOnly(contact);
  const offered = !profile && revealOffered(contact, 'profile', status, workspaceId);
  const missed = profile ? null : revealMissedAt(contact, 'profile');
  const websiteFor = (firm: string | null) =>
    sameFirmName(firm, contact.account_name) ? contact.account_website : null;

  if (employment.length === 0 && !view && !offered && !missed) {
    return <EmptyTabState message="No experience on file yet" />;
  }
  return (
    <div className="ph-no-capture space-y-6 text-sm [&>*+*]:border-t [&>*+*]:pt-6">
      {employment.length > 0 || view ? (
        <Block
          title="Experience"
          aside={
            view?.connections != null ? (
              <span className="text-xs text-muted-foreground">
                {`${view.connections.toLocaleString('en-US')} connections`}
              </span>
            ) : null
          }
        >
          {employment.length > 0 ? (
            <ul className="space-y-5">
              {groupByCompany(employment).map((group, index) => (
                <CompanyBlock
                  // biome-ignore lint/suspicious/noArrayIndexKey: provider roles carry no id and may repeat.
                  key={index}
                  group={group}
                  website={
                    group.roles.find((role) => role.firm_website)?.firm_website ??
                    websiteFor(group.firm)
                  }
                />
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No employment history</p>
          )}
        </Block>
      ) : null}
      {offered ? (
        <div>
          <section className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed p-4">
            <p className="text-muted-foreground">
              Every past role, education, skills and languages.
            </p>
            <RevealAction contact={contact} kind="profile" />
          </section>
        </div>
      ) : null}
      {missed ? (
        <div>
          <section className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed p-4">
            <RevealMissed
              kind="profile"
              label="No LinkedIn profile found"
              tip={`Checked ${shortDate(missed)}.`}
            />
            <span className="text-xs text-muted-foreground">
              {`Try again ${shortDate(windowEnd(missed, LOOKUP_RETRY_DAYS).toISOString())}`}
            </span>
          </section>
        </div>
      ) : null}
      {view && view.education.length > 0 ? (
        <Block title="Education">
          <ul className="space-y-5">
            {view.education.map((entry, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: provider entries carry no id and may repeat.
              <School key={index} entry={entry} />
            ))}
          </ul>
        </Block>
      ) : null}
      {view && view.skills.length > 0 ? (
        <Block title="Skills">
          <Chips items={[...new Set(view.skills)].map((skill) => ({ key: skill, label: skill }))} />
        </Block>
      ) : null}
      {view && view.languages.length > 0 ? (
        <Block title="Languages">
          <Chips
            items={view.languages.map((language, index) => ({
              key: `${language.name}#${index}`,
              label: language.name,
              note: language.level,
            }))}
          />
        </Block>
      ) : null}
    </div>
  );
}
